/* eslint-disable function-paren-newline */
import { useCallback, useEffect, useRef } from "react";
import { LAYER_TYPE } from "../../actions/Layers/constants";
import apiClient from "../../constants/apiClient";
import { ROOT_URL } from "../../constants/urls";

/**
 * search for layers
 */
export const searchLayers = (value: string, type?: LAYER_TYPE | null, parent_layer_id?: string) => {
    return apiClient.get(`${ROOT_URL}api/layers/search/`, {
        params: {
            text: value,
            layer_type: type,
            parent_layer_id,
        }
    })
        .then((response) => {
            return response.data.map((i) => ({
                ...i, value: i.label, label: i.label, type: i.type,
            }));
        })
        .catch((error) => {
            throw new Error(`Could not fetch layers ${error}`);
        });
};


/**
 * Custom hook for debouncing a value change handler.
 *
 * On unmount the hook FLUSHES any still-pending trailing call (i.e. fires
 * onDebounce synchronously) rather than cancelling it. Cancelling would
 * silently drop the user's last edit on the durable side (online: server
 * PATCH; offline: capture-into-IDB) when they navigate away within
 * `debounceTime`. Flushing on unmount also fixes the offline race where
 * a still-armed timer fires after the network is back, dispatching a
 * PATCH /avocados/{temp_id} that 404s — the trailing fire now happens
 * while we're still in the offline window, so the offline write-capture
 * layer claims it as expected.
 *
 * @param setValue - The state change function to set the value immediately.
 * @param onDebounce - The callback function to be called after debounce time.
 * @param debounceTime - The debounce time in milliseconds (default is 1000).
 * @returns The debounced value change handler.
 */
export function useDebounceForObject(
    setValue: (field: string, value: any) => void,
    onDebounce: (field: string, value: any) => void,
    debounceTime = 1000
): (field: string, value: any) => void {
    const debounceTimer = useRef<{ [key: string]: number | undefined }>({});
    // Per-field pending trailing callback. We store the bound callback
    // (with its captured field+value) rather than the args so unmount
    // flush can replay it without needing the latest closure.
    type FlushCallback = () => void;
    const pendingFlush = useRef<{ [key: string]: FlushCallback | undefined }>({});

    const handleValueChange = useCallback((field: string, value: any) => {
        // Immediately dispatch the state change
        setValue(field, value);

        // Clear any previous timers for this field
        if (debounceTimer.current[field]) {
            clearTimeout(debounceTimer.current[field]);
        }

        // Set a new timer for this field. We share one closure between
        // the timer body and the unmount flusher so either path clears
        // both the timer ref and the pending callback after firing.
        const fire = () => {
            debounceTimer.current[field] = undefined;
            pendingFlush.current[field] = undefined;
            onDebounce(field, value);
        };
        pendingFlush.current[field] = fire;
        debounceTimer.current[field] = setTimeout(fire, debounceTime) as unknown as number;
    }, [onDebounce, setValue, debounceTime]);

    // Flush pending trailing calls on unmount. We read from the refs so
    // the deps stay empty — this effect must run exactly once at unmount,
    // not on every re-render (which would prematurely fire trailing calls
    // whenever onDebounce / setValue identity changed).
    useEffect(() => {
        return () => {
            Object.keys(debounceTimer.current).forEach((field) => {
                const timer = debounceTimer.current[field];
                if (timer !== undefined) {
                    clearTimeout(timer);
                }
                pendingFlush.current[field]?.();
            });
            debounceTimer.current = {};
            pendingFlush.current = {};
        };
    }, []);

    return handleValueChange;
}

export type UseDebouncedReturnType<T extends (...args: any[]) => void> = [(...args: Parameters<T>) => void, (...args: Parameters<T>) => void];

/**
 * Custom hook to debounce a function call.
 *
 * @template T - Type of the value being debounced.
 * @param {Function} setValue - Function to set the value immediately.
 * @param {Function} onDebounce - Function to call after debounce time.
 * @param {Object} options - Options for debounce.
 * @param {number} [options.debounceTime=1000] - Time in milliseconds to debounce.
 * @param {boolean} [options.immediate=false] - Whether to call the function immediately on the leading edge.
 * @returns {Function} - Debounced function.
 */
// eslint-disable-next-line space-before-function-paren, function-paren-newline
export function useDebounce<T extends (...args: any[]) => void>(
    setValue: T,
    onDebounce: T,
    { debounceTime = 1000, immediate = false }: { debounceTime?: number, immediate?: boolean }
): UseDebouncedReturnType<T> {
    const debounceTimer = useRef<number | undefined>();
    const immediateCalled = useRef<boolean>(false);

    // Cancel — do NOT flush — the pending trailing call on unmount. This hook
    // debounces *fetches* (its one consumer is useSwitchTab's listLayers), and
    // a fetch for a page you've already left has no value. Worse, it's harmful:
    // the stale trailing dispatch fired LIST.LOADING up to `debounceTime` after
    // leaving the overview, flipping the global layers.isLoading while the
    // add-check scene was mounting — which swaps the whole fruit form for a
    // spinner and back (Check/ManualForm gate on it), silently unmounting any
    // open modal and killing clicks in flight. (Contrast useDebounceForObject
    // above, which FLUSHES on unmount because it debounces durable writes.)
    useEffect(() => () => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
    }, []);

    const callDebounced = useCallback((...args: Parameters<T>) => {
        // Immediately dispatch the state change
        setValue(...args);

        // Clear any previous timers
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (immediate && !immediateCalled.current) {
            // If immediate is true and the function hasn't been called immediately yet
            onDebounce(...args);
            immediateCalled.current = true;
        } else {
            // Set a new timer for the trailing edge
            debounceTimer.current = setTimeout(() => {
                onDebounce(...args);
                immediateCalled.current = false; // Reset for the next call
            }, debounceTime) as unknown as number;
        }
    }, [onDebounce, setValue, debounceTime, immediate]);
    const callAsIfWaited = useCallback((...args: Parameters<T>) => {
        // Immediately dispatch the state change
        setValue(...args);

        // Clear any previous timers
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (!immediateCalled.current) {
            // If immediate is true and the function hasn't been called immediately yet
            onDebounce(...args);
        }
    }, [onDebounce, setValue]);
    return [callDebounced, callAsIfWaited];
}

export type UseDebouncedAsyncReturnType<T extends (...args: any[]) => Promise<any>> = [(...args: Parameters<T>) => Promise<any>, (...args: Parameters<T>) => Promise<any>];

export function useDebounceFunc<T extends(...args: any[]) => any>(
    onDebounce: T,
    { debounceTime = 1000, immediate = false }: { debounceTime?: number, immediate?: boolean }
): UseDebouncedAsyncReturnType<T> {
    const debounceTimer = useRef<number | undefined>();
    const immediateCalled = useRef<boolean>(false);

    const callDebounced = useCallback((...args: Parameters<T>) => {
        return new Promise((resolve) => {
            // Clear any previous timers
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            if (immediate && !immediateCalled.current) {
                // If immediate is true and the function hasn't been called immediately yet
                const result = onDebounce(...args);
                immediateCalled.current = true;
                resolve(result);
            } else {
                // Set a new timer for the trailing edge
                debounceTimer.current = setTimeout(() => {
                    const result = onDebounce(...args);
                    resolve(result);
                }, debounceTime) as unknown as number;
            }
        });
    }, [onDebounce, debounceTime, immediate]);
    const callAsIfWaited = useCallback((...args: Parameters<T>) => {
        return new Promise((resolve) => {
            // Clear any previous timers
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            const result = onDebounce(...args);
            resolve(result);
        });
    }, [onDebounce]);
    return [callDebounced, callAsIfWaited];
}

export const useSearchLayersDebounced = () => useDebounceFunc((value: string, type?: LAYER_TYPE | null, parent_layer_id?: string) => searchLayers(value, type, parent_layer_id), { debounceTime: 650 });
